<template>
  <div id="linechart-container" ref="chart"></div>
</template>

<script>
import dimple from "dimple";
import { Utils } from "../../ts/utils";

export default {
  props: {
    compareYear: Number,
    currentYear: Number,
    historicalYear: Number,
    compareWijkScore: Number,
    currentWijkScore: Number,
    historicalWijkScore: Number,
    compareAvgScore: Number,
    currentAvgScore: Number,
    historicalAvgScore: Number,
    isPercentage: {
      type: Boolean,
      default: false,
    },
    eenheid: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      chart: null,
      svg: null,
    };
  },
  mounted() {
    this.renderChart();
  },
  computed: {
    computedScores() {
      return [
        this.historicalWijkScore,
        this.compareWijkScore,
        this.currentWijkScore,
        this.historicalAvgScore,
        this.compareAvgScore,
        this.currentAvgScore,
      ];
    },
    validScores() {
      return this.computedScores.filter(
        (s) => s !== undefined && s !== null && !isNaN(s)
      );
    },
    yAxisMin() {
      if (!this.validScores.length) return 0;
      const min = Math.min(...this.validScores);
      const max = Math.max(...this.validScores);
      const range = max - min;

      // Calculate padding based on range and data characteristics
      // For small ranges, use minimum padding of 2 (for percentages) or 10% of range
      // For larger ranges, use 15% of range but cap at reasonable limits
      let padding;
      if (this.isPercentage) {
        padding = Math.max(2, Math.min(range * 0.15, 10));
      } else {
        padding = Math.max(range * 0.15, min * 0.1);
      }

      const paddedMin = Math.floor(min - padding);
      // Never go below 0
      return Math.max(0, paddedMin);
    },
    yAxisMax() {
      if (!this.validScores.length) return 10;
      const min = Math.min(...this.validScores);
      const max = Math.max(...this.validScores);
      const range = max - min;

      // Calculate padding based on range and data characteristics
      // For small ranges, use minimum padding of 2 (for percentages) or 15% of range
      // For larger ranges, use 15% of range but cap at reasonable limits
      let padding;
      if (this.isPercentage) {
        padding = Math.max(2, Math.min(range * 0.15, 10));
      } else {
        padding = Math.max(range * 0.15, max * 0.1);
      }

      const paddedMax = Math.ceil(max + padding);
      // Cap at 100 for percentage scores
      return this.isPercentage ? Math.min(paddedMax, 100) : paddedMax;
    },
    isDistanceIndicator() {
      // Check if this is a distance-related indicator based on the eenheid field
      return Utils.isDistance(this.eenheid);
    },
  },
  methods: {
    formatScore(score) {
      if (this.isDistanceIndicator) {
        // For distance data, use 1 decimal place
        return parseFloat(score).toFixed(1);
      } else if (this.isPercentage) {
        // For percentage data, round to whole numbers
        return Math.round(score);
      } else if (this.eenheid && (this.eenheid.includes("per woning") || this.eenheid.includes("per m2"))) {
        // For score data, use progressive rounding based on magnitude
        const numScore = parseFloat(score);
        let roundedValue;
        if (numScore >= 100000) {
          // For 100k+, round to nearest 1000
          roundedValue = Math.round(numScore / 1000) * 1000;
        } else if (numScore >= 10000) {
          // For 10k-99k, round to nearest 100
          roundedValue = Math.round(numScore / 100) * 100;
        } else if (numScore >= 1000) {
          // For 1k-9k, round to nearest 10
          roundedValue = Math.round(numScore / 10) * 10;
        } else {
          // For <1k, round to whole numbers
          roundedValue = Math.round(numScore);
        }
        return roundedValue;
      } else {
        // For other data, round to whole numbers
        return Math.round(score);
      }
    },

    renderChart() {
      const data = [];

      if (this.historicalAvgScore !== undefined && this.historicalAvgScore !== null && !isNaN(this.historicalAvgScore)) {
        data.push({
          label: "gemeente",
          jaar: this.historicalYear,
          score: this.formatScore(this.historicalAvgScore),
        });
      }
      if (this.historicalWijkScore !== undefined && this.historicalWijkScore !== null && !isNaN(this.historicalWijkScore)) {
        data.push({
          label: "wijk",
          jaar: this.historicalYear,
          score: this.formatScore(this.historicalWijkScore),
        });
      }
      if (this.compareAvgScore !== undefined && this.compareAvgScore !== null && !isNaN(this.compareAvgScore)) {
        data.push({
          label: "gemeente",
          jaar: this.compareYear,
          score: this.formatScore(this.compareAvgScore),
        });
      }
      if (this.compareWijkScore !== undefined && this.compareWijkScore !== null && !isNaN(this.compareWijkScore)) {
        data.push({
          label: "wijk",
          jaar: this.compareYear,
          score: this.formatScore(this.compareWijkScore),
        });
      }
      if (this.currentAvgScore !== undefined && this.currentAvgScore !== null && !isNaN(this.currentAvgScore)) {
        data.push({
          label: "gemeente",
          jaar: this.currentYear,
          score: this.formatScore(this.currentAvgScore),
        });
      }
      if (this.currentWijkScore !== undefined && this.currentWijkScore !== null && !isNaN(this.currentWijkScore)) {
        data.push({
          label: "wijk",
          jaar: this.currentYear,
          score: this.formatScore(this.currentWijkScore),
        });
      }


      const container = d3.select("#linechart-container");
      container.html("");

      this.svg = dimple.newSvg("#linechart-container", "100%", "100%");
      this.chart = new dimple.chart(this.svg, data);
      this.chart.setMargins("40px", "10px", "10px", "60px");

      const x = this.chart.addCategoryAxis("x", "jaar");
      const y = this.chart.addMeasureAxis("y", "score");

      // Set explicit min/max before drawing
      y.overrideMin = this.yAxisMin;
      y.overrideMax = this.yAxisMax;

      // Suppress Dimple tick labels
      y.tickFormat = "";

      const series = this.chart.addSeries("label", dimple.plot.line);
      series.lineWeight = 3;
      series.lineMarkers = true;

      // Add custom tooltip formatting
      series.getTooltipText = (e) => {
        const tooltip = [];
        tooltip.push(`${e.aggField[0]}`); // Series label (wijk/gemiddeld)
        tooltip.push(`${e.x}`); // Year
        tooltip.push(`${this.formatScore(e.y)}`); // Formatted score value
        return tooltip;
      };

      const legend = this.chart.addLegend("100%", 0, 100, 50, "right");
      legend.x = 0;
      legend.y = 120;
      legend.horizontalAlign = "right";

      this.chart.assignColor("wijk", "#ef9f92");
      this.chart.assignColor("gemiddeld", "#a1c0d9");

      this.chart.draw();

      // Make overlapping markers distinguishable and hoverable
      if (this.svg) {
        const markers = this.svg.selectAll("circle.dimple-marker");
        const markersByYear = {};

        // Group markers by year (x position)
        markers.each(function (d) {
          if (d && d.x) {
            const year = d.x;
            if (!markersByYear[year]) {
              markersByYear[year] = [];
            }
            markersByYear[year].push({
              element: this,
              data: d,
              cx: parseFloat(d3.select(this).attr("cx")),
              cy: parseFloat(d3.select(this).attr("cy"))
            });
          }
        });

        // Check each year for overlapping markers
        Object.keys(markersByYear).forEach(year => {
          const yearMarkers = markersByYear[year];
          if (yearMarkers.length === 2) {
            const marker1 = yearMarkers[0];
            const marker2 = yearMarkers[1];

            // Check if markers are at same Y position (within 1 pixel tolerance)
            if (Math.abs(marker1.cy - marker2.cy) < 1) {
              // Make markers distinguishable: wijk solid, gemiddeld hollow ring
              let wijkMarker, gemiddeldMarker;

              if (marker1.data.aggField && marker1.data.aggField[0] === "wijk") {
                wijkMarker = marker1;
                gemiddeldMarker = marker2;
              } else {
                wijkMarker = marker2;
                gemiddeldMarker = marker1;
              }

              // Gemiddeld: larger hollow ring (drawn first, behind)
              d3.select(gemiddeldMarker.element)
                .attr("r", 8)
                .style("fill", "none")
                .style("stroke", "#a1c0d9")
                .style("stroke-width", "3px")
                .lower();

              // Wijk: smaller solid circle on top
              d3.select(wijkMarker.element)
                .attr("r", 4.5)
                .style("fill", "#ef9f92")
                .style("stroke", "white")
                .style("stroke-width", "2px")
                .raise();
            }
          }
        });
      }

      // Hide all Dimple y-axis labels after chart is drawn
      if (y.shapes) {
        y.shapes.selectAll("text").style("display", "none");
      }

      // Also hide any other y-axis text elements
      if (this.svg) {
        this.svg.selectAll('.dimple-y-axis text').style("display", "none");
      }

      // Hide horizontal grid lines
      if (y.gridlineShapes) {
        y.gridlineShapes.selectAll("line").style("display", "none");
      }

      // Also hide any other grid lines
      if (this.svg) {
        this.svg.selectAll('.dimple-gridline').style("display", "none");
      }

      // Style x-axis labels to match y-axis
      if (x.shapes) {
        x.shapes.selectAll("text")
          .style("fill", "black")
          .style("font-size", "12px");
      }

      // Add custom y-axis with only min and max
      const yScale = d3
        .scaleLinear()
        .domain([this.yAxisMin, this.yAxisMax])
        .range([this.chart._yPixels() + this.chart._heightPixels(), this.chart._yPixels()]);

      const yAxis = d3.axisLeft(yScale)
        .tickValues([this.yAxisMin, this.yAxisMax])
        .tickFormat(d3.format("d"));

      if (this.svg) {
        this.svg
          .append("g")
          .attr("class", "custom-y-axis")
          .attr("transform", `translate(${this.chart._xPixels()},0)`)
          .call(yAxis)
          .selectAll("text")
          .style("fill", "black")
          .style("font-size", "10px");
      }
    },
    destroy() {
      if (this.svg) {
        this.svg.selectAll("*").remove();
      }
    },
    refresh() {
      if (this.svg) {
        this.destroy();
        this.renderChart();
      }
    },
  },
  beforeDestroy() {
    this.destroy();
  },
  watch: {
    computedScores() {
      this.refresh();
    },
  },
};
</script>

<style scoped>
.custom-y-axis text {
  fill: black;
  font-size: 12px;
}

/* Hide all Dimple y-axis labels */
.dimple-y-axis text {
  display: none !important;
}
</style>
